import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { PaymentIntent } from '@stripe/stripe-js'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import {
  resolveConfidenceVisitorId,
  VISITOR_ID_QUERY_PARAM,
} from '../helpers/confidenceVisitorId'
import { trackSubscriptionEvent } from '../helpers/facebookTracking'
import { getStripePriceAmountAndCurrency } from '../helpers/localizeStripePrice'
import { GetPricesData } from '../loaders/getPricesLoader'
import { Plan } from '../types'
import Button from './Button'
import OrderSummary from './OrderSummary'
import TermsCheckbox from './TermsCheckbox'

interface Props {
  clientSecret: string
  currency: string
  intentMode: 'payment' | 'setup'
  plan: Plan
  pricesData: GetPricesData
  subscriptionId: string
  trialPeriodDays?: number
  userEmail: string | undefined
}

export default function CheckoutForm({
  clientSecret,
  currency,
  intentMode,
  plan,
  pricesData,
  subscriptionId,
  trialPeriodDays,
  userEmail,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false)
  const [message, setMessage] = useState<string | undefined>(undefined)
  const [isIntentReady, setIsIntentReady] = useState(false)
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | undefined>(
    undefined,
  )

  const elements = useElements()
  const location = useLocation()
  const stripe = useStripe()
  const { t } = useTranslation()

  const stripePriceFull =
    plan === 'one-time'
      ? pricesData.oneTimePrice
      : plan === 'top'
        ? pricesData.topPrice
        : plan === 'first'
          ? pricesData.firstPrice
          : pricesData.secondPrice

  const stripePriceIntro =
    plan === 'one-time'
      ? undefined
      : plan === 'top'
        ? pricesData.topIntroPrice
        : plan === 'first'
          ? pricesData.firstIntroPrice
          : pricesData.secondIntroPrice

  const stripePrice = stripePriceIntro ?? stripePriceFull

  // Calculate full price from stripe price (undefined if price data not available)
  const fullPriceData =
    stripePriceFull && currency
      ? getStripePriceAmountAndCurrency({
          stripePrice: stripePriceFull,
          userCurrency: currency,
        })
      : undefined

  const fullPrice = fullPriceData?.stripePriceAmountInCents
  const fullPriceCurrency = fullPriceData?.stripePriceCurrency

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!elements || !stripe) return

    setIsSubmitting(true)

    const origin = window.location.origin
    const pathname = '/checkout/payment-completion'
    const urlSearchParams = new URLSearchParams(location.search)

    urlSearchParams.append('stripe_first_price_id', stripePriceIntro?.id ?? '')
    urlSearchParams.append('stripe_next_price_id', stripePriceFull?.id ?? '')
    if (subscriptionId) {
      urlSearchParams.append('subscription_id', subscriptionId)
    }

    // Confirming payment hands the browser to Stripe, and coming back is a
    // fresh page load that has to find the visitor id again. Measured on Lingo
    // web, 23% of purchases find nothing there and mint a new id, so the
    // purchase lands on a visitor with no funnel behind it. Passing it through
    // the return URL is the only carrier the browser cannot drop.
    //
    // Cleared before writing, and written with `set` rather than `append`,
    // because these params start as a copy of the current URL's. A visitor
    // arriving on an attacker-crafted `?cnfd_visitor_id=` would otherwise carry
    // it into the return URL, and since `URLSearchParams.get` returns the first
    // of a duplicated key, theirs would win over ours on the way back. The
    // delete also covers the case where we have no id to send: an inbound one
    // must not survive on its own.
    urlSearchParams.delete(VISITOR_ID_QUERY_PARAM)

    const confidenceVisitorId = resolveConfidenceVisitorId()
    if (confidenceVisitorId) {
      urlSearchParams.set(VISITOR_ID_QUERY_PARAM, confidenceVisitorId)
    }

    const returnUrl = `${origin}${pathname}?${urlSearchParams.toString()}`

    const { error } =
      intentMode === 'setup'
        ? await stripe.confirmSetup({
            elements,
            confirmParams: { return_url: returnUrl },
          })
        : await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: returnUrl },
          })

    setIsSubmitting(false)

    if (!error) return

    if (error.type === 'card_error' || error.type === 'validation_error') {
      setMessage(error.message)
    } else {
      throw error
    }
  }

  useEffect(() => {
    if (!clientSecret || !stripe) {
      return
    }

    const retrieveIntent = async () => {
      if (intentMode === 'setup') {
        const { setupIntent } = await stripe.retrieveSetupIntent(clientSecret)
        setIsIntentReady(Boolean(setupIntent))
        if (setupIntent?.last_setup_error) {
          setMessage(t('components.checkoutForm.somethingWentWrong'))
        }
        return
      }

      const { paymentIntent: retrievedPaymentIntent } =
        await stripe.retrievePaymentIntent(clientSecret)
      setPaymentIntent(retrievedPaymentIntent)
      setIsIntentReady(Boolean(retrievedPaymentIntent))

      if (retrievedPaymentIntent?.last_payment_error) {
        if (
          retrievedPaymentIntent.last_payment_error.code ===
          'payment_intent_authentication_failure'
        ) {
          setMessage(t('components.checkoutForm.paymentFailed'))
        } else {
          setMessage(t('components.checkoutForm.somethingWentWrong'))
        }
      }
    }

    retrieveIntent().catch((error) => {
      throw error
    })
  }, [clientSecret, intentMode, stripe, t])

  useEffect(() => {
    if (!isIntentReady) return

    const trackEvent = async () => {
      await trackSubscriptionEvent({
        currency,
        eventType: 'initiate-checkout',
        stripeFirstPriceId: stripePriceIntro?.id ?? '',
        stripeNextPriceId: stripePriceFull?.id ?? '',
      })
    }

    trackEvent().catch((error) => console.error(error))
  }, [currency, isIntentReady, stripePriceFull?.id, stripePriceIntro?.id])

  useEffect(() => {
    const resetSubmittingState = () => {
      setIsSubmitting(false)
    }

    window.addEventListener('pageshow', resetSubmittingState)
    document.addEventListener('visibilitychange', resetSubmittingState)

    return () => {
      window.removeEventListener('pageshow', resetSubmittingState)
      document.removeEventListener('visibilitychange', resetSubmittingState)
    }
  }, [])

  return (
    <form
      className="flex h-full flex-col justify-between space-y-4"
      onSubmit={handleSubmit}
    >
      <PaymentElement
        className="rounded-3xl border bg-white p-5"
        options={{
          defaultValues: {
            billingDetails: {
              email: userEmail,
            },
          },
          layout: {
            defaultCollapsed: false,
            type: 'tabs',
          },
          paymentMethodOrder: [
            'link',
            'apple_pay',
            'paypal',
            'card',
            'google_pay',
          ],
        }}
      />

      <div className="space-y-6">
        <OrderSummary
          fullPrice={fullPrice}
          fullPriceCurrency={fullPriceCurrency}
          paymentIntent={paymentIntent}
          plan={plan}
          stripePrice={stripePrice}
          stripePriceFull={stripePriceFull}
          stripePriceIntro={stripePriceIntro}
          trialPeriodDays={trialPeriodDays}
        />

        <TermsCheckbox
          fullPrice={fullPrice}
          fullPriceCurrency={fullPriceCurrency}
          isTermsAccepted={isTermsAccepted}
          paymentIntent={paymentIntent}
          plan={plan}
          stripePriceFull={stripePriceFull}
          setIsTermsAccepted={setIsTermsAccepted}
          trialPeriodDays={trialPeriodDays}
        />

        {message && (
          <p className="text-custom-error text-center text-lg font-bold">
            {message}
          </p>
        )}

        <Button
          dataTestId="button-pay"
          disabled={!elements || !stripe || !isTermsAccepted}
          submitting={isSubmitting}
          title={
            trialPeriodDays
              ? t('pages.checkout.trialPlans.startTrial')
              : plan === 'one-time'
                ? t('buttons.payNow')
                : t('buttons.subscribeNow')
          }
          type="submit"
        />

        <p className="text-center text-xs">
          {t('pages.checkout.payment.termsCheckboxHint')}
        </p>
      </div>
    </form>
  )
}
